import {
  Button,
  CircularProgress,
  Link,
  Text,
  Tooltip,
} from '@shared/components';
import { ActionExecutionStatusIcon } from '@shared/components/actionExecutionStatusIcon/ActionExecutionStatusIcon';
import {
  ActionExecutionEventType,
  ActionExecutionStatus,
  CommunicationChannel,
} from '@shared/generated/graphql';
import { useActionDefinitionPreview } from '@shared/graphql/hooks/actionDefinitions/useActionDefinitionPreview';
import {
  ActionExecutionEvent,
  ActionExecutionEventData,
  AgentReasoningEventData,
  isRunningToolCall,
  MemoryRecalledEventData,
  StatusChangedEventData,
  SupervisorReasoningEventData,
  SupervisorToolUsedEventData,
  ToolUsedEventData,
} from '@shared/types/action-execution';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { QueryParamName } from 'clerk_common/constants/urls/queryParams';
import { TASKS_ROUTE } from 'clerk_common/constants/urls/routes';
import { assertUnreachable } from 'clerk_common/enum';
import { enumToString } from 'clerk_common/enums';
import { handlePlural } from 'clerk_common/stringification/handlePlural';
import { formatAbsoluteTime } from 'clerk_common/stringification/times';
import {
  getSkillToolCallId,
  SKILL_TOOL_ACTIONS,
  SkillToolAction,
} from 'clerk_common/stringification/toolCalls';
import clsx from 'clsx';
import { camelCase, isNil, startCase } from 'lodash';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { BsFillQuestionCircleFill } from 'react-icons/bs';
import { FaBug } from 'react-icons/fa';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { LiaClockSolid, LiaToolsSolid } from 'react-icons/lia';
import { MdForkRight, MdOpenInNew } from 'react-icons/md';
import { PiBrain, PiPhoneSlashBold } from 'react-icons/pi';
import { TbAlertTriangleFilled } from 'react-icons/tb';
import { VscCommentDraft } from 'react-icons/vsc';
import { EditableText } from './EditableText';
import { ExecuteToolButton } from './ExecuteToolButton';
import { RawMessageEventContents } from './RawMessageEventContents';

const ROOT = makeRootClassName('ActionExecutionEvent');
const el = makeElementClassNameFactory(ROOT);
const COLLAPSED_TOOL_OUTPUT_LINE_COUNT = 5;
const LONG_TOOL_OUTPUT_CHARACTER_COUNT = 1200;

type ActionExecutionEventProps = {
  event: ActionExecutionEvent;
  isModified?: boolean;
  isEditable?: boolean;
  onEditEventData?: (updatedData: ActionExecutionEventData) => void;
};

export const ActionExecutionEventComponent = ({
  event,
  isModified,
  isEditable,
  onEditEventData,
}: ActionExecutionEventProps) => {
  return (
    <div className={clsx(el`ae-event`, isModified && el`ae-event--modified`)}>
      {isModified && (
        <span className={el`ae-event-modified-label`}>modified</span>
      )}
      <ActionExecutionEventHeader event={event} />
      <ActionExecutionEventContents
        event={event}
        isEditable={isEditable}
        onEditEventData={isEditable ? onEditEventData : undefined}
      />
    </div>
  );
};

type HeaderProps = {
  event: ActionExecutionEvent;
};

export const ActionExecutionEventHeader = ({ event }: HeaderProps) => {
  return (
    <div className={el`ae-event-header`}>
      <div className="flex items-center gap-2">
        <span className="flex-shrink-0">
          <ActionExecutionEventIcon event={event} />
        </span>
        <EventTitle event={event} />
      </div>
      <div className="flex-1" />
      <Tooltip content={event.createdAt.toISOString()}>
        <div className="flex items-center gap-2">
          <Text type="body-xs"> {formatAbsoluteTime(event.createdAt)}</Text>
        </div>
      </Tooltip>
    </div>
  );
};

function EventTitle({ event }: HeaderProps) {
  const skillToolCall = getSkillToolCall(event);
  if (!skillToolCall) {
    return <Text type="body-sm">{stringifyEventName(event)}</Text>;
  }
  return <SkillToolUsedEventTitle event={event} {...skillToolCall} />;
}

function getSkillToolCall(
  event: ActionExecutionEvent
): { action: SkillToolAction; skillId: string } | null {
  if (isNil(event.data) || !isToolUsedEventData(event.data)) return null;
  const action = SKILL_TOOL_ACTIONS[event.data.toolName];
  const skillId = getSkillToolCallId(event.data.toolInput);
  return action && skillId ? { action, skillId } : null;
}

function SkillToolUsedEventTitle({
  event,
  action,
  skillId,
}: {
  event: ActionExecutionEvent;
  action: SkillToolAction;
  skillId: string;
}) {
  const { actionDefinition } = useActionDefinitionPreview({ id: skillId });
  const actor =
    event.name === ActionExecutionEventType.SUPERVISOR_TOOL_USED
      ? 'Supervisor'
      : 'Agent';
  const title = actionDefinition?.name
    ? `${actor} ${action.toLowerCase()} skill - ${actionDefinition.name}`
    : stringifyEventName(event);
  return <Text type="body-sm">{title}</Text>;
}

export const ActionExecutionEventIcon = ({
  event,
}: {
  event: ActionExecutionEvent;
}) => {
  switch (event.name) {
    case ActionExecutionEventType.ESCALATION:
      return <TbAlertTriangleFilled color="orange" />;
    case ActionExecutionEventType.DECISION_EDGE_EVALUATED:
      return <MdForkRight />;
    case ActionExecutionEventType.TOOL_USED:
      return <LiaToolsSolid />;
    case ActionExecutionEventType.SUPERVISOR_TOOL_USED:
      return <LiaToolsSolid color="orange" />;
    case ActionExecutionEventType.RAW_AGENT_MESSAGE:
      return <FaBug />;
    case ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE:
      return <FaBug color="orange" />;
    case ActionExecutionEventType.AGENT_REASONING:
      return <HiOutlineLightBulb />;
    case ActionExecutionEventType.SUPERVISOR_REASONING:
      return <HiOutlineLightBulb color="orange" />;
    case ActionExecutionEventType.MEMORY_RECALLED:
      return <PiBrain />;
    case ActionExecutionEventType.UNKNOWN:
      return <BsFillQuestionCircleFill />;
    case ActionExecutionEventType.SPAM_GUARD:
      return <PiPhoneSlashBold />;
    case ActionExecutionEventType.REMINDER:
      return <LiaClockSolid />;
    case ActionExecutionEventType.INSTRUCTION_MESSAGE:
      return <VscCommentDraft />;
    case ActionExecutionEventType.STATUS_CHANGED:
      return <StatusChangedEventIcon event={event} />;
    case ActionExecutionEventType.AGENT_PROPOSED_OUTPUT:
      return <VscCommentDraft />;
    case ActionExecutionEventType.SUPERVISOR_PROPOSED_OUTPUT:
      return <VscCommentDraft color="orange" />;
    case ActionExecutionEventType.WATCHER_CLOSED:
    case ActionExecutionEventType.WATCHER_POKED:
      return <PiPhoneSlashBold color="gray" />;
    default:
      return assertUnreachable(event.name);
  }
};

function StatusChangedEventIcon({ event }: { event: ActionExecutionEvent }) {
  if (isNil(event.data)) return null;
  if (!isStatusChangedEventData(event.data)) return null;
  if (isNil(event.data.toStatus)) return null;
  return <ActionExecutionStatusIcon status={event.data.toStatus} size={14} />;
}

function isToolUsedEventData(
  data: ActionExecutionEventData
): data is ToolUsedEventData | SupervisorToolUsedEventData {
  if (isNil(data)) return false;
  return [
    ActionExecutionEventType.TOOL_USED,
    ActionExecutionEventType.SUPERVISOR_TOOL_USED,
  ].includes(data.eventDataType);
}

function isStatusChangedEventData(
  data: ActionExecutionEventData
): data is StatusChangedEventData {
  if (isNil(data)) return false;
  return data.eventDataType === ActionExecutionEventType.STATUS_CHANGED;
}

function isReasoningEventData(
  data: ActionExecutionEventData
): data is AgentReasoningEventData | SupervisorReasoningEventData {
  if (isNil(data)) return false;
  return [
    ActionExecutionEventType.AGENT_REASONING,
    ActionExecutionEventType.SUPERVISOR_REASONING,
  ].includes(data.eventDataType);
}

export const stringifyEventName = (event: ActionExecutionEvent) => {
  const name = event.name;
  switch (name) {
    case ActionExecutionEventType.ESCALATION:
      return 'Escalation';
    case ActionExecutionEventType.DECISION_EDGE_EVALUATED:
      return 'Decision branch evaluated';
    case ActionExecutionEventType.TOOL_USED:
      return toToolUsedEventTitle({ event });
    case ActionExecutionEventType.SUPERVISOR_TOOL_USED:
      return toToolUsedEventTitle({ event });
    case ActionExecutionEventType.RAW_AGENT_MESSAGE:
      return 'Raw agent message';
    case ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE:
      return 'Raw supervisor message';
    case ActionExecutionEventType.AGENT_REASONING:
      return 'Agent reasoning';
    case ActionExecutionEventType.SUPERVISOR_REASONING:
      return 'Supervisor reasoning';
    case ActionExecutionEventType.MEMORY_RECALLED: {
      const memories = event.data as MemoryRecalledEventData;
      return handlePlural(
        memories.memories?.length || 0,
        'Memory recalled',
        'Memories recalled',
        false
      );
    }
    case ActionExecutionEventType.UNKNOWN:
      return 'Unknown event';
    case ActionExecutionEventType.SPAM_GUARD:
      return 'Spam guardrail';
    case ActionExecutionEventType.REMINDER:
      return 'Time elapsed';
    case ActionExecutionEventType.INSTRUCTION_MESSAGE:
      return 'Instruction received';
    case ActionExecutionEventType.STATUS_CHANGED:
      return stringifyStatusChangedEventName({ event });
    case ActionExecutionEventType.AGENT_PROPOSED_OUTPUT:
      return 'Agent proposed output';
    case ActionExecutionEventType.SUPERVISOR_PROPOSED_OUTPUT:
      return 'Supervisor proposed output';
    case ActionExecutionEventType.WATCHER_CLOSED:
      return 'Watcher closed';
    case ActionExecutionEventType.WATCHER_POKED:
      return 'Watcher poked';
    default:
      return assertUnreachable(name);
  }
};

function stringifyStatusChangedEventName({
  event,
}: {
  event: ActionExecutionEvent;
}) {
  if (isNil(event.data)) return 'Status changed';
  if (!isStatusChangedEventData(event.data)) return 'Status changed';
  return stringifyStatusChange(event.data);
}

function statusChangePhrase(data: StatusChangedEventData): string {
  switch (data.toStatus) {
    case ActionExecutionStatus.COMPLETED:
      return 'completed';
    case ActionExecutionStatus.FAILED:
      return 'failed';
    case ActionExecutionStatus.IN_PROGRESS:
      return [
        ActionExecutionStatus.NEW,
        ActionExecutionStatus.SCHEDULED,
        ActionExecutionStatus.PROPOSED,
      ].includes(data.fromStatus)
        ? 'started'
        : 'resumed';
    case ActionExecutionStatus.DELAYING:
      return 'delaying';
    case ActionExecutionStatus.WAITING:
      return 'is waiting';
    case ActionExecutionStatus.NEW:
      return 'created';
    case ActionExecutionStatus.PROPOSED:
      return 'proposed';
    case ActionExecutionStatus.REJECTED:
      return 'rejected';
    case ActionExecutionStatus.SCHEDULED:
      return 'scheduled';
    case ActionExecutionStatus.TIMEOUT:
      return 'timed out';
    default:
      return assertUnreachable(data.toStatus);
  }
}

export function stringifyStatusChange(
  data: StatusChangedEventData,
  actionDefinitionName?: string
): string {
  const subject = actionDefinitionName
    ? `Task '${actionDefinitionName}'`
    : 'Task';
  return `${subject} ${statusChangePhrase(data)}`;
}

function toToolUsedEventTitle({ event }: { event: ActionExecutionEvent }) {
  const prefix = toToolUsedEventPrefix({ event });
  if (isNil(event.data)) return 'Tool used';
  if (!isToolUsedEventData(event.data)) return 'Tool used';
  return `${prefix} ${startCase(camelCase(event.data.toolName))}`;
}

function toToolUsedEventPrefix({ event }: { event: ActionExecutionEvent }) {
  switch (event.name) {
    case ActionExecutionEventType.TOOL_USED:
      return 'Agent used tool:';
    case ActionExecutionEventType.SUPERVISOR_TOOL_USED:
      return 'Supervisor used tool:';
    default:
      return 'Tool used:';
  }
}

type ContentsProps = {
  event: ActionExecutionEvent;
  isEditable?: boolean;
  onEditEventData?: (updatedData: ActionExecutionEventData) => void;
};

export const ActionExecutionEventContents = ({
  event,
  isEditable,
  onEditEventData,
}: ContentsProps) => {
  if (isNil(event.data)) return null;
  switch (event.name) {
    case ActionExecutionEventType.ESCALATION:
      return <EscalationEventContents data={event.data} />;
    case ActionExecutionEventType.DECISION_EDGE_EVALUATED:
      return <DecisionEdgeEvaluatedEventContents data={event.data} />;
    case ActionExecutionEventType.TOOL_USED:
    case ActionExecutionEventType.SUPERVISOR_TOOL_USED:
      return (
        <ToolUsedEventContents
          data={event.data}
          isEditable={isEditable}
          onEditEventData={onEditEventData}
        />
      );
    case ActionExecutionEventType.RAW_AGENT_MESSAGE:
      return (
        <RawMessageEventContents
          data={event.data}
          isEditable={isEditable}
          onEditEventData={onEditEventData}
        />
      );
    case ActionExecutionEventType.RAW_SUPERVISOR_MESSAGE:
      return <RawMessageEventContents data={event.data} />;
    case ActionExecutionEventType.AGENT_REASONING:
    case ActionExecutionEventType.SUPERVISOR_REASONING:
      return (
        <AgentReasoningEventContents
          data={event.data}
          isEditable={isEditable}
          onEditEventData={onEditEventData}
        />
      );
    case ActionExecutionEventType.SPAM_GUARD:
      return <SpamGuardEventContents data={event.data} />;
    case ActionExecutionEventType.MEMORY_RECALLED:
      return <RecalledMemoryLink data={event.data} />;
    case ActionExecutionEventType.REMINDER:
      return <ReminderEventContents data={event.data} />;
    case ActionExecutionEventType.INSTRUCTION_MESSAGE:
      return <InstructionMessageEventContents data={event.data} />;
    case ActionExecutionEventType.UNKNOWN:
      return (
        <div>
          {event.id} {event.name}
        </div>
      );
    case ActionExecutionEventType.STATUS_CHANGED:
      return null;
    case ActionExecutionEventType.AGENT_PROPOSED_OUTPUT:
      return (
        <AgentProposedOutputEventContents
          data={event.data}
          isEditable={isEditable}
          onEditEventData={onEditEventData}
        />
      );
    case ActionExecutionEventType.SUPERVISOR_PROPOSED_OUTPUT:
      return <SupervisorProposedOutputEventContents data={event.data} />;
    case ActionExecutionEventType.WATCHER_CLOSED:
    case ActionExecutionEventType.WATCHER_POKED:
      return null;
    default:
      return assertUnreachable(event.name);
  }
};

function EscalationEventContents({ data }: { data: ActionExecutionEventData }) {
  if (data.eventDataType !== ActionExecutionEventType.ESCALATION) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 p-1 italic">
      <Text type="body-xs" isHeavy>
        {enumToString(data.escalationType)}
      </Text>
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">{data.message}</Text>
      </pre>
      {data.codeExecutionError && (
        <pre className="whitespace-pre-wrap">
          <Text type="body-xs">{data.codeExecutionError}</Text>
        </pre>
      )}
    </div>
  );
}

function DecisionEdgeEvaluatedEventContents({
  data,
}: {
  data: ActionExecutionEventData;
}) {
  if (data.eventDataType !== ActionExecutionEventType.DECISION_EDGE_EVALUATED) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 p-1 italic">
      {data.targetNodeActionDefinitionId && (
        <TargetNodeName
          targetNodeActionDefinitionId={data.targetNodeActionDefinitionId}
        />
      )}
      <div className="flex flex-col gap-1">
        <Text type="body-xs" isHeavy>
          Reasoning:
        </Text>
        <Text type="body-xs">{data.rationale}</Text>
      </div>
      <div className="flex items-center gap-2">
        <Text type="body-xs" isHeavy>
          Decision:
        </Text>
        <Text type="body-xs">{data.shouldFollowEdge ? '✅' : '❌'}</Text>
        <Text type="body-xs">
          {data.shouldFollowEdge ? 'Take branch' : 'Avoid branch'}
        </Text>
      </div>
    </div>
  );
}

function TargetNodeName({
  targetNodeActionDefinitionId,
}: {
  targetNodeActionDefinitionId: string;
}) {
  const { actionDefinition, isLoading } = useActionDefinitionPreview({
    id: targetNodeActionDefinitionId,
  });
  return (
    <div className="flex items-center gap-2">
      <Text type="body-xs" isHeavy>
        Considering branch:
      </Text>
      {isLoading ? (
        <CircularProgress size="xs" />
      ) : (
        <Text type="body-xs">{actionDefinition?.name}</Text>
      )}
    </div>
  );
}

function ToolUsedEventContents({
  data,
  isEditable,
  onEditEventData,
}: {
  data: ActionExecutionEventData;
  isEditable?: boolean;
  onEditEventData?: (updatedData: ActionExecutionEventData) => void;
}) {
  if (!isToolUsedEventData(data)) return null;

  const formattedInput = formatToolValue(data.toolInput);
  const formattedOutput = formatToolValue(data.toolOutput);

  const handleEditOutput = (newValue: string) => {
    if (!onEditEventData) return;
    try {
      const parsedOutput = JSON.parse(newValue);
      onEditEventData({ ...data, toolOutput: parsedOutput });
    } catch {
      onEditEventData({ ...data, toolOutput: { result: newValue } });
    }
  };

  return (
    <div className="relative flex flex-col gap-1 p-1 italic">
      {isEditable && onEditEventData && (
        <div className="flex items-center justify-end">
          <ExecuteToolButton data={data} onEditEventData={onEditEventData} />
        </div>
      )}
      <Text type="body-xs" isHeavy>
        Input:
      </Text>
      <EditableText value={formattedInput} />
      <Text type="body-xs" isHeavy>
        Output:
      </Text>
      {isRunningToolCall(data) ? (
        <Text type="body-xs">Running…</Text>
      ) : (
        <CollapsibleToolOutput value={formattedOutput}>
          <EditableText
            value={formattedOutput}
            isEditable={isEditable}
            onEdit={handleEditOutput}
          />
        </CollapsibleToolOutput>
      )}
    </div>
  );
}

function formatToolValue(value: unknown) {
  if (typeof value !== 'string') {
    return JSON.stringify(value, null, 2) ?? String(value);
  }

  try {
    const parsedValue = JSON.parse(value);
    if (typeof parsedValue === 'object' && parsedValue !== null) {
      return JSON.stringify(parsedValue, null, 2);
    }
  } catch {
    return value;
  }

  return value;
}

function CollapsibleToolOutput({
  value,
  children,
}: {
  value: string;
  children: ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLongOutput =
    value.length > LONG_TOOL_OUTPUT_CHARACTER_COUNT ||
    value.split('\n').length > COLLAPSED_TOOL_OUTPUT_LINE_COUNT;

  if (!isLongOutput) return <>{children}</>;

  return (
    <div className="flex flex-col gap-1">
      <div
        className={clsx(
          'relative overflow-hidden',
          !isExpanded && 'max-h-48 rounded-b-md'
        )}
      >
        {children}
        {!isExpanded && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-white/90" />
        )}
      </div>
      <div className="flex justify-center">
        <Button
          size="xs"
          isGhost
          onPress={() => setIsExpanded((expanded) => !expanded)}
          labelClassName="text-gray-400"
        >
          {isExpanded ? '- Show less' : '+ Show more'}
        </Button>
      </div>
    </div>
  );
}

function AgentReasoningEventContents({
  data,
  isEditable,
  onEditEventData,
}: {
  data: ActionExecutionEventData;
  isEditable?: boolean;
  onEditEventData?: (updatedData: ActionExecutionEventData) => void;
}) {
  if (!isReasoningEventData(data)) return null;

  const handleEditReasoning = (newValue: string) => {
    if (!onEditEventData) return;
    onEditEventData({ ...data, reasoning: newValue });
  };

  return (
    <div className="flex flex-col gap-1 p-1 italic">
      <EditableText
        value={data.reasoning}
        isEditable={isEditable}
        onEdit={handleEditReasoning}
      />
    </div>
  );
}

function SpamGuardEventContents({ data }: { data: ActionExecutionEventData }) {
  if (data.eventDataType !== ActionExecutionEventType.SPAM_GUARD) {
    return null;
  }

  const message =
    data.communicationChannel === CommunicationChannel.PHONE_CALL
      ? 'Spam guardrail prevented phone call:'
      : 'Spam guardrail prevented continuation:';
  return (
    <div className="flex flex-col gap-1 p-1 italic">
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">{`${message}`}</Text>
        <Text type="body-xs">{`From: ${data.fromIdentifier}`}</Text>
        <Text type="body-xs">{`To: ${data.toIdentifier}`}</Text>
        <Text type="body-xs">{`Reason: ${data.reason}`}</Text>
      </pre>
    </div>
  );
}

export function RecalledMemoryLink({
  data,
}: {
  data: ActionExecutionEventData;
}) {
  if (data.eventDataType !== ActionExecutionEventType.MEMORY_RECALLED) {
    return null;
  }
  if (!data.memories?.length) {
    return (
      <Text type="body-xs" className="text-gray-500">
        No memories
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-1 p-1">
      {data.memories.map((m) => (
        <div key={m.id} className="flex flex-row items-start gap-2">
          <Link
            size="small"
            target="_blank"
            href={`/memories?${QueryParamName.MEMORY_ID}=${m.id}`}
          >
            <Tooltip content="View memory" isInstant>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{m.id.slice(0, 8)}</code>
                <MdOpenInNew />
              </div>
            </Tooltip>
          </Link>
          <Text type="body-xs" className="italic">
            {m.comment || 'N/A'}
          </Text>
        </div>
      ))}
    </div>
  );
}

function ReminderEventContents({ data }: { data: ActionExecutionEventData }) {
  if (data.eventDataType !== ActionExecutionEventType.REMINDER) {
    return null;
  }

  const message = 'message' in data ? data.message : 'Reminder event';

  return (
    <div className="flex flex-col gap-1 p-1 italic">
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">{message}</Text>
      </pre>
    </div>
  );
}

export function InstructionMessageEventContents({
  data,
}: {
  data: ActionExecutionEventData;
}) {
  if (data.eventDataType !== ActionExecutionEventType.INSTRUCTION_MESSAGE) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 p-1">
      {data.sourceActionExecutionId && (
        <SourceTaskLink actionExecutionId={data.sourceActionExecutionId} />
      )}
      <pre className="whitespace-pre-wrap italic">
        <Text type="body-xs">{data.message}</Text>
      </pre>
    </div>
  );
}

function SourceTaskLink({ actionExecutionId }: { actionExecutionId: string }) {
  return (
    <Link
      size="small"
      target="_blank"
      href={`${TASKS_ROUTE}?view=all&${QueryParamName.TASK_ID}=${actionExecutionId}&tab=details`}
    >
      <Tooltip content="View the task that sent this" isInstant>
        <div className="flex items-center gap-1">
          <Text type="body-xs">From task</Text>
          <code className="font-mono text-xs">
            {actionExecutionId.slice(0, 8)}
          </code>
          <MdOpenInNew />
        </div>
      </Tooltip>
    </Link>
  );
}

function SupervisorProposedOutputEventContents({
  data,
}: {
  data: ActionExecutionEventData;
}) {
  if (
    data.eventDataType !== ActionExecutionEventType.SUPERVISOR_PROPOSED_OUTPUT
  ) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 p-1 italic">
      <pre className="whitespace-pre-wrap">
        <Text type="body-xs">
          {typeof data.output === 'string'
            ? data.output
            : JSON.stringify(data.output, null, 2)}
        </Text>
      </pre>
    </div>
  );
}

function AgentProposedOutputEventContents({
  data,
  isEditable,
  onEditEventData,
}: {
  data: ActionExecutionEventData;
  isEditable?: boolean;
  onEditEventData?: (updatedData: ActionExecutionEventData) => void;
}) {
  if (data.eventDataType !== ActionExecutionEventType.AGENT_PROPOSED_OUTPUT) {
    return null;
  }

  const formattedOutput =
    typeof data.output === 'string'
      ? data.output
      : JSON.stringify(data.output, null, 2);

  const handleEditOutput = (newValue: string) => {
    if (!onEditEventData) return;
    try {
      const parsedOutput = JSON.parse(newValue);
      onEditEventData({ ...data, output: parsedOutput });
    } catch {
      onEditEventData({ ...data, output: newValue });
    }
  };

  return (
    <div className="flex flex-col gap-1 p-1">
      <EditableText
        value={formattedOutput}
        isEditable={isEditable}
        onEdit={handleEditOutput}
      />
    </div>
  );
}
